
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { HeroSection } from "@/components/specializations/nistcsf/HeroSection";
import { Helmet } from "react-helmet";
import { ClipboardCheck, ShieldCheck, BarChart3, FileCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ValueMetricsSection } from "@/components/services/specializations/ValueMetricsSection";
import { HiddenCapabilitiesSection } from "@/components/services/specializations/HiddenCapabilitiesSection";
import { PlatformValueSection } from "@/components/services/specializations/PlatformValueSection";

const NISTCsf = () => {
  // Value metrics data
  const valueMetrics = [
    {
      value: "87%",
      label: "Risk Reduction",
      description: "Average reduction in security incidents after NIST CSF implementation"
    },
    {
      value: "65%",
      label: "Tools Coverage",
      description: "Average percentage of NIST CSF controls covered by existing technology"
    },
    {
      value: "40%",
      label: "Operation Efficiency",
      description: "Improvement in security operations efficiency with proper NIST alignment"
    },
    {
      value: "3.5x",
      label: "ROI",
      description: "Average return on investment from NIST CSF implementation"
    }
  ];

  // Hidden capabilities data
  const hiddenCapabilities = [
    {
      title: "Existing Tool Integration",
      description: "Most organizations already own security tools that can be configured to satisfy many NIST CSF requirements without additional purchases.",
      percentage: 65
    },
    {
      title: "M365 Security Features",
      description: "Microsoft 365 Enterprise includes native security capabilities that map directly to NIST CSF requirements across all five functions.",
      percentage: 70
    },
    {
      title: "Automation Potential",
      description: "Security processes can be automated using existing tools, dramatically improving efficiency and consistency of NIST CSF controls.",
      percentage: 45
    },
    {
      title: "Reporting Capabilities",
      description: "Your existing platforms likely have untapped reporting capabilities that can demonstrate NIST CSF compliance to stakeholders.",
      percentage: 40
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        {/* Meta tags */}
      </Helmet>
      
      <Navbar />
      
      <main className="relative">
        {/* Hero Section */}
        <HeroSection />
        
        {/* Value Metrics Section */}
        <ValueMetricsSection 
          title="Maximize Security ROI with NIST CSF"
          subtitle="Business Impact"
          description="Our NIST CSF implementation approach delivers measurable security improvements while leveraging your existing technology investments."
          metrics={valueMetrics}
        />

        {/* Hidden Capabilities Section */}
        <HiddenCapabilitiesSection 
          platform="Your Current Tools"
          capabilities={hiddenCapabilities}
        />

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Service Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Assessment Card */}
              <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <ClipboardCheck className="h-12 w-12 text-purple-600 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-semibold mb-3">NIST CSF Assessment</h3>
                <p className="text-gray-600 mb-4">
                  Comprehensive evaluation of your current security posture against NIST CSF, identifying gaps and optimization opportunities.
                </p>
                <Button variant="outline" className="w-full group">
                  Learn More 
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Button>
              </div>

              {/* Implementation Card */}
              <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <ShieldCheck className="h-12 w-12 text-purple-600 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-semibold mb-3">Implementation Support</h3>
                <p className="text-gray-600 mb-4">
                  Expert guidance on implementing NIST CSF controls using your existing technology stack, maximizing your current investments.
                </p>
                <Button variant="outline" className="w-full group">
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Button>
              </div>

              {/* Reporting Card */}
              <div className="p-6 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <BarChart3 className="h-12 w-12 text-purple-600 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-semibold mb-3">Compliance Reporting</h3>
                <p className="text-gray-600 mb-4">
                  Automated dashboards and reporting tools that demonstrate your NIST CSF compliance posture to stakeholders and auditors.
                </p>
                <Button variant="outline" className="w-full group">
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>

          {/* NIST CSF Functions Section */}
          <div id="framework" className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
                NIST CSF Core Functions
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Our approach helps you implement all five NIST CSF functions while maximizing the value of your existing security tools.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-16">
              {/* NIST CSF Functions */}
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <ShieldCheck className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Identify</h3>
                <p className="text-gray-600">
                  Develop organizational understanding to manage cybersecurity risk to systems, assets, data, and capabilities.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <ShieldCheck className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Protect</h3>
                <p className="text-gray-600">
                  Develop and implement appropriate safeguards to ensure delivery of critical infrastructure services.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <ShieldCheck className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Detect</h3>
                <p className="text-gray-600">
                  Develop and implement appropriate activities to identify the occurrence of a cybersecurity event.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <ShieldCheck className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Respond</h3>
                <p className="text-gray-600">
                  Develop and implement appropriate activities to take action regarding a detected cybersecurity incident.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <ShieldCheck className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Recover</h3>
                <p className="text-gray-600">
                  Develop and implement appropriate activities to maintain plans for resilience and to restore capabilities or services.
                </p>
              </div>
            </div>
          </div>

          {/* Platform Value Section */}
          <PlatformValueSection
            title="Leverage Existing Tools for NIST CSF"
            subtitle="Value Maximization"
            description="Our approach helps you implement NIST CSF by optimizing the security tools you already own, saving you from unnecessary technology investments."
            benefits={[
              {
                title: "Microsoft 365 Security Integration",
                description: "Map Microsoft 365 security features directly to NIST CSF controls, maximizing your existing license value."
              },
              {
                title: "Existing Tool Configuration",
                description: "Optimize your current security tools to satisfy NIST requirements without purchasing new solutions."
              },
              {
                title: "Automation & Orchestration",
                description: "Create automated workflows that enforce NIST controls using tools you already own."
              },
              {
                title: "Compliance Reporting",
                description: "Generate comprehensive compliance reports from your existing security data sources."
              }
            ]}
            imageSrc="/lovable-uploads/5b81b7cb-e3f2-43ac-afb8-7947fa058e75.png"
            imageAlt="NIST CSF framework implementation using existing tools"
            linkUrl="/get-started"
            linkText="Schedule a NIST CSF Assessment"
          />

          {/* Case Studies Section */}
          <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12 mb-16">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl text-center mb-12">
                Success Stories
              </h2>
              
              <div className="grid grid-cols-1 gap-8">
                {/* Healthcare Provider Case Study */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="md:grid md:grid-cols-3 items-center">
                    <div className="hidden md:block relative h-64 md:h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/30 to-blue-100/30 mix-blend-multiply" />
                      <img
                        src="/lovable-uploads/e40665b8-7f09-40d0-99d6-186ca019f985.png"
                        alt="Healthcare provider implementing NIST CSF"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20" />
                    </div>
                    <div className="col-span-2 p-8 space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">Regional Healthcare System</h3>
                          <p className="text-purple-600 font-medium">1,500+ Employees</p>
                        </div>
                        <div className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                          90% Tool Reuse
                        </div>
                      </div>
                      <p className="text-gray-600">
                        Implemented NIST CSF controls by optimizing existing Microsoft 365 E5 licenses and security tools, achieving HIPAA compliance without additional technology investments.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Mapped 80% of NIST CSF controls to existing Microsoft 365 security features</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Created automated compliance dashboards using Power BI</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Reduced security incidents by 65% in the first year</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Services Case Study */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="md:grid md:grid-cols-3 items-center">
                    <div className="hidden md:block relative h-64 md:h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/30 to-blue-100/30 mix-blend-multiply" />
                      <img
                        src="/lovable-uploads/81fc9627-891a-487d-ad5e-dfa2d54b4264.png"
                        alt="Financial services firm implementing NIST CSF"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20" />
                    </div>
                    <div className="col-span-2 p-8 space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">Multi-State Credit Union</h3>
                          <p className="text-purple-600 font-medium">500+ Employees</p>
                        </div>
                        <div className="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                          40% Cost Savings
                        </div>
                      </div>
                      <p className="text-gray-600">
                        Achieved NIST CSF alignment by optimizing their existing security infrastructure, avoiding costly new security tools while satisfying regulatory requirements.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Reconfigured existing SIEM to provide NIST CSF-aligned reporting</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Integrated Microsoft Defender with third-party compliance tools</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" aria-hidden="true" />
                          <p className="text-gray-600">Created a unified security dashboard for both NIST CSF and FFIEC compliance</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-purple-600 rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Implement NIST CSF with Tools You Already Own?
              </h2>
              <p className="text-purple-100 mb-8 max-w-2xl mx-auto">
                Schedule a consultation to discover how we can help you achieve NIST CSF compliance while maximizing your existing technology investments.
              </p>
              <Link to="/get-started">
                <Button 
                  className="bg-white text-purple-600 hover:bg-purple-50 transition-colors duration-300"
                  size="lg"
                >
                  Schedule a Consultation
                </Button>
              </Link>
            </div>
          </div>

          {/* Features Section */}
          <div className="mt-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-white to-purple-50/30">
              <div className="absolute inset-0 bg-white/40 backdrop-blur-3xl" />
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-purple-100/30 to-transparent" />
              
              <div className="relative py-20 px-8 md:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <div className="space-y-10">
                    <div className="space-y-6">
                      <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        NIST CSF Implementation Approach
                      </h2>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        Our proven methodology helps organizations implement NIST CSF efficiently by leveraging existing security investments rather than purchasing new tools.
                      </p>
                    </div>

                    <div className="space-y-8 relative">
                      <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-purple-400 to-purple-100 rounded-full" aria-hidden="true" />
                      <div className="pl-8">
                        <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                          Our Process
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          We follow a structured, proven approach to NIST CSF implementation:
                        </p>
                        <ul className="mt-6 space-y-5">
                          <li className="flex items-start gap-4">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <ClipboardCheck className="h-5 w-5 text-purple-600" aria-hidden="true" />
                            </div>
                            <span className="text-gray-600 leading-relaxed mt-1">Comprehensive assessment of current security posture against NIST CSF</span>
                          </li>
                          <li className="flex items-start gap-4">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <FileCheck className="h-5 w-5 text-purple-600" aria-hidden="true" />
                            </div>
                            <span className="text-gray-600 leading-relaxed mt-1">Mapping of existing tools to NIST CSF requirements</span>
                          </li>
                          <li className="flex items-start gap-4">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <BarChart3 className="h-5 w-5 text-purple-600" aria-hidden="true" />
                            </div>
                            <span className="text-gray-600 leading-relaxed mt-1">Development of metrics and reporting for continuous improvement</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <Link 
                      to="/resources/how-to"
                      className="group inline-flex items-center gap-3 px-6 py-3 bg-purple-50 hover:bg-purple-100 text-purple-600 font-semibold rounded-full transition-colors duration-300"
                    >
                      Explore Our Resources
                      <ArrowRight 
                        className="h-5 w-5 transition-transform duration-300 transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </Link>
                  </div>

                  <div className="relative">
                    <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-10 border border-purple-100/50 shadow-[0_4px_20px_-4px_rgba(107,70,193,0.15)] transition-all duration-500 hover:shadow-[0_8px_30px_-4px_rgba(107,70,193,0.25)]">
                      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-100/30 to-blue-100/30 rounded-full blur-3xl transform -translate-x-20 -translate-y-20" aria-hidden="true" />
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-100/30 to-purple-100/30 rounded-full blur-3xl transform translate-x-10 translate-y-10" aria-hidden="true" />
                      
                      <div className="relative">
                        <h3 className="text-2xl font-semibold text-gray-900 mb-8">
                          Benefits of Our Approach
                        </h3>
                        <div className="space-y-5">
                          <div className="p-6 bg-gradient-to-br from-purple-50/70 to-white rounded-xl border border-purple-100/20">
                            <h4 className="font-medium text-gray-900">Cost Efficiency</h4>
                            <p className="text-gray-600 mt-2">Maximize existing security investments instead of buying new tools</p>
                          </div>
                          <div className="p-6 bg-gradient-to-br from-blue-50/70 to-white rounded-xl border border-blue-100/20">
                            <h4 className="font-medium text-gray-900">Faster Implementation</h4>
                            <p className="text-gray-600 mt-2">Achieve compliance more quickly by leveraging familiar tools</p>
                          </div>
                          <div className="p-6 bg-gradient-to-br from-green-50/70 to-white rounded-xl border border-green-100/20">
                            <h4 className="font-medium text-gray-900">Sustainable Compliance</h4>
                            <p className="text-gray-600 mt-2">Build compliance into your existing processes for long-term success</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NISTCsf;
